import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import type { TAppContext, TAppState, TRange, TUser, TView } from 'src/types';
import isEqual from 'lodash/isEqual';
import unionWith from 'lodash/unionWith';
import get from 'lodash/get';
import { DEFAULT_TIME_ZONE } from 'src/constants';
import { useAuth } from 'src/utils/auth';
import { usePrevious } from 'src/hooks/usePrevious';
import { useAppToaster } from './useAppToaster';
import { useAppModal } from './useAppModal';

export const defaultState: TAppState = {
    range: {
        from: DateTime.local().setZone(DEFAULT_TIME_ZONE).startOf('week'),
        to: DateTime.local().setZone(DEFAULT_TIME_ZONE).endOf('week'),
    },
    location: null,
    subLocation: null,
    view: 'me', // all, me, my-people
    users: [],
};

export const useAppContext = (): TAppContext => {
    const { user: userData } = useAuth();
    const user = get(userData, 'profile.user', null);
    const timeZone = get(user, 'timezone', DEFAULT_TIME_ZONE);

    const { notification, handleCallNotification } = useAppToaster();
    const { modalData, handleCallModal, handleCloseModal } = useAppModal();
    const [state, setState] = useState<TAppState>({
        ...defaultState,
        range: {
            from: DateTime.local().setZone(timeZone).startOf('week'),
            to: DateTime.local().setZone(timeZone).endOf('week'),
        },
    });

    const prevTz = usePrevious(timeZone);
    useEffect(() => {
        if (prevTz !== timeZone) {
            setState({
                ...state,
                range: {
                    from: DateTime.local().setZone(timeZone).startOf('week'),
                    to: DateTime.local().setZone(timeZone).endOf('week'),
                },
            });
        }
    }, [prevTz, state, timeZone]);

    const handleDateRangeChange = (range: TRange) => {
        setState({
            ...state,
            range,
        });
    };

    const handleLocationChange = (locationId: string) => {
        setState({
            ...state,
            location: locationId,
            subLocation: null,
        });
    };

    const handleSubLocationChange = (subLocationId: string | null) => {
        setState({
            ...state,
            subLocation: subLocationId,
        });
    };

    const handleViewChange = (view: TView) => {
        setState({
            ...state,
            view,
        });
    };

    const handleSetUsers = (usersList: TUser[]) => {
        const grouped = unionWith(state.users, usersList, isEqual);
        setState({
            ...state,
            users: grouped,
        });
    };

    return {
        values: {
            ...state,
            notification,
            modalData,
        },
        actions: {
            handleDateRangeChange,
            handleLocationChange,
            handleSubLocationChange,
            handleViewChange,
            handleCallNotification,
            handleCallModal,
            handleCloseModal,
            handleSetUsers,
        },
    };
};
